<template>
  <div class="ledger-detail">
    <div class="table-responsive">
      <table class="table table-sm mb-0 align-middle">
        <thead>
          <tr>
            <th>Account</th>
            <th class="text-end">Amount ({{ entry.currency }})</th>
            <th>Effect</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(line, index) in entry.lines" :key="index">
            <td
              class="text-nowrap"
              :title="line.account"
              data-test="line-account"
            >
              <span
                v-if="!line.is_company_line"
                class="badge text-bg-secondary me-2"
                >Soldera</span
              >{{ humanize(line.account) }}
            </td>
            <td class="text-end fw-semibold text-nowrap tabular-amount">
              {{ formatSignedAmount(line.amount) }}
            </td>
            <td
              :class="
                line.is_company_line ? 'fw-semibold' : 'text-body-secondary'
              "
            >
              {{ lineEffect(line.account, line.amount) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex flex-column gap-3 fs-9">
      <dl
        v-if="
          entry.reverses_entry_id ||
          entry.reversed_by_entry_id ||
          entry.invoice_links.length
        "
        class="ledger-meta mb-0"
      >
        <template v-if="entry.reverses_entry_id">
          <dt class="text-body-secondary fw-normal">Reverses</dt>
          <dd class="mb-0">
            <a
              href="#"
              class="font-monospace"
              @click.prevent="$emit('go:entry', entry.reverses_entry_id)"
              >#{{ entry.reverses_entry_id }}</a
            >
          </dd>
        </template>
        <template v-if="entry.reversed_by_entry_id">
          <dt class="text-body-secondary fw-normal">Reversed by</dt>
          <dd class="mb-0">
            <a
              href="#"
              class="font-monospace"
              @click.prevent="$emit('go:entry', entry.reversed_by_entry_id)"
              >#{{ entry.reversed_by_entry_id }}</a
            >
          </dd>
        </template>
        <template
          v-for="link in entry.invoice_links"
          :key="`${link.kind}-${link.invoice_id}`"
        >
          <dt
            class="text-body-secondary fw-normal"
            :title="link.kind"
            data-test="invoice-link"
          >
            {{ LINK_LABELS[link.kind] }}
          </dt>
          <dd class="mb-0">
            <span class="fw-semibold">{{ link.invoice_number }}</span>
            <span class="text-body-secondary">
              · {{ humanize(link.status) }}</span
            >
          </dd>
        </template>
      </dl>

      <div v-if="entry.has_sources">
        <div class="text-body-secondary fw-semibold mb-1">Source</div>
        <div v-if="sources === undefined" class="text-body-secondary">
          Loading…
        </div>
        <div v-else-if="sources.length === 0" class="text-body-secondary">
          No source rows
        </div>
        <dl
          v-for="card in sources"
          v-else
          :key="`${card.table}-${card.id}`"
          class="ledger-meta mb-2"
        >
          <dt class="fw-semibold">{{ card.title }}</dt>
          <dd class="mb-0 font-monospace">
            <RouterLink
              v-if="card.table === 'sales_transaction'"
              :to="{
                name: 'AdminTransactionsEditView',
                params: { id: card.id },
              }"
              >#{{ card.id }}</RouterLink
            >
            <span v-else class="text-body-secondary">#{{ card.id }}</span>
          </dd>
          <template v-for="field in card.fields" :key="field.key">
            <dt class="text-body-secondary fw-normal">{{ field.key }}</dt>
            <dd class="mb-0 fw-semibold text-break">{{ field.value }}</dd>
          </template>
        </dl>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { lineEffect } from "./ledgerEffects";
import { formatSignedAmount, humanize } from "./ledgerFormat";
import type {
  InvoiceEntryLinkKind,
  LedgerEntry,
  LedgerSourceCard,
} from "./ledgerTypes";

defineProps<{
  entry: LedgerEntry;
  /** `undefined` while the sources are still loading. */
  sources?: LedgerSourceCard[];
}>();

defineEmits<{
  "go:entry": [entryId: number];
}>();

// Each link read from the entry's side: an invoice RENDERS or SETTLES an entry, and a payment
// entry is what an invoice is SETTLED_BY.
const LINK_LABELS: Record<InvoiceEntryLinkKind, string> = {
  RENDERS: "Shown on",
  SETTLES: "Settled by",
  SETTLED_BY: "Pays",
};
</script>

<style lang="scss" scoped>
.ledger-detail {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(16rem, 2fr);
  gap: 2rem;
  align-items: start;

  @media (max-width: 991.98px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ledger-meta {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.25rem 1.5rem;
}

.tabular-amount {
  font-variant-numeric: tabular-nums;
}
</style>
